/**
 * Supabase Realtime singleton used only for the "station:broadcast" channel.
 *
 * We avoid supabase-js elsewhere (see lib/supabase.ts) — every Station edge
 * function call goes through lib/api.ts so we control headers consistently.
 * Realtime needs a real client though, so this is the one allowed exception:
 * a single shared instance, no auth session, broadcast-only.
 *
 * Used to coordinate "everyone check for a new build NOW" instead of waiting
 * for the 60 s SW update poll in UpdatePrompt. The channel is pure ephemeral
 * pub/sub — no DB tables, no RLS, no Postgres at all.
 */
import {
  createClient,
  type RealtimeChannel,
  type SupabaseClient,
} from "@supabase/supabase-js";
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from "./supabase";

const CHANNEL_NAME = "station:broadcast";

export const BROADCAST_EVENT_FORCE_UPDATE = "force_update_check" as const;
export const BROADCAST_EVENT_MANDATORY_RELOAD = "mandatory_reload" as const;

let client: SupabaseClient | null = null;
let channel: RealtimeChannel | null = null;

function getClient(): SupabaseClient {
  if (!client) {
    client = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
    });
  }
  return client;
}

/** Returns the singleton broadcast channel. Caller must `.subscribe()` once. */
export function getBroadcastChannel(): RealtimeChannel {
  if (!channel) {
    channel = getClient().channel(CHANNEL_NAME, {
      // self:false — sender shouldn't trigger its own prompt.
      config: { broadcast: { ack: false, self: false } },
    });
  }
  return channel;
}
